/**
 * BOQ Summary Generator
 * Generates Bill of Quantities summaries independent of Plot Matrix
 */

import { generateRateFromFieldName, calculateFieldAmount } from './rateCalculationUtils';
import groundworksRatesMappingJSON from './groundworks_rates_mapping.json';
import { RateMappingJSON } from '../types/rateMappingTypes';
import { transformPlotDrivesData, PLOT_DRIVES_REVERSE_MAPPING } from './plotDrivesColumnMapping';
import { getPlotDrivesFieldLabel, getPlotDrivesFieldUnit } from './plotDrivesFieldMapping';
import { getDrainageFieldLabel, getDrainageFieldUnit } from './drainageFieldMapping';
import { getPlotServiceFieldLabel, getPlotServiceFieldUnit } from './plotServiceFieldMapping';
import { PLOT_PAVINGS_REVERSE_MAPPING } from './plotPavingsColumnMapping';
import { getPlotPavingsFieldLabel, getPlotPavingsFieldUnit } from './plotPavingsFieldMapping';
import { SCAFFOLD_REVERSE_MAPPING } from './scaffoldColumnMapping';
import { getScaffoldFieldLabel, getScaffoldFieldUnit } from './scaffoldFieldMapping';
import { GARDEN_REVERSE_MAPPING } from './gardenColumnMapping';
import { getGardenFieldLabel, getGardenFieldUnit } from './gardenFieldMapping';
import { FOUNDATIONS_REVERSE_MAPPING } from './foundationsColumnMapping';
import { getFoundationsFieldLabel, getFoundationsFieldUnit } from './foundationsFieldMapping';
import { getRetainingWallsFieldLabel, getRetainingWallsFieldUnit } from './retainingWallsFieldMapping';
import { getGeoToberWallFieldLabel, getGeoToberWallFieldUnit } from './geoToberWallFieldMapping';
import { formatDimensionLabel, normaliseDecimalMmInLabel } from './dimensionLabel';

/**
 * Foundation fields that should have rates ignored (set to 0)
 * These are:
 * - Dense concrete blockwork 100mm (Ext leaf) fields
 * - Aerated concrete blockwork 100mm (Ext leaf) fields
 * - Aerated HB common brickwork 100mm field
 */
const FOUNDATIONS_ZERO_RATE_FIELDS = [
  'dense4NBlockwork100mm',
  'dense7NBlockwork100mm',
  'dense10NBlockwork100mm',
  'aerated4NBlockwork100mm',
  'aerated7NBlockwork100mm',
  'aerated100mmCoursingBrick',
];

export interface BOQColumn {
  key: string;
  fieldName: string;
  label: string;
  unit: string;
  rate: number;
  stepName: string;
}

export interface BOQSummaryItem {
  key: string;
  fieldName: string;
  description: string;
  quantity: number;
  unit: string;
  rate: number;
  amount: number;
  category?: string;
}

/**
 * Helper: Generate readable label from field name
 */
function generateLabelFromFieldName(fieldName: string): string {
  // Convert camelCase to Title Case
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

/**
 * Helper: Remove unit suffix from label (e.g., "(m)", "(nr)", "Nr").
 * Exported so other surfaces (e.g. the Plot Matrix Uncategorized info
 * modal) can render labels that match what the BoQ Bill Summaries
 * actually display — they all apply this transformation before showing
 * the description.
 */
export function removeUnitFromLabel(label: string): string {
  // Remove units in parentheses like "(m)", "(nr)", "(m²)", etc.
  let cleanLabel = label.replace(/\s*\([^)]*\)\s*$/g, '').trim();
  // Remove "Nr" or "nr" at the end
  cleanLabel = cleanLabel.replace(/\s+Nr\s*$/gi, '').trim();
  return cleanLabel;
}

/**
 * Helper: Substitute xx/xxx dimension placeholders in a label with the actual value
 * from dimensionValues, falling back to the columnHeader if provided.
 * Mirrors the getColumnTitle() logic used in form components.
 */
function resolveDynamicLabel(
  staticLabel: string,
  fieldName: string,
  dimensionValues: Record<string, string> | undefined,
  columnHeaders: Record<string, string> | undefined,
): string {
  const colHeader = columnHeaders?.[fieldName];
  // If a resolved column header exists and it is NOT still a placeholder, use it directly.
  // Repair any persisted decimal-mm patterns (e.g. "0.150mm" → "150mm") on the way out.
  if (colHeader && !/(x{2,}mm?)/i.test(colHeader)) {
    return normaliseDecimalMmInLabel(removeUnitFromLabel(colHeader));
  }
  // Substitute a raw dimension value (e.g. "150") into xxx/xx placeholders.
  // Decimal entries (e.g. "0.150") are converted to mm for display via
  // `formatDimensionLabel` so the bill summary always shows millimetre values.
  const dimValue = dimensionValues?.[fieldName];
  const base = removeUnitFromLabel(staticLabel);
  if (dimValue && /(x{2,}mm?)/i.test(base)) {
    const normalised = formatDimensionLabel(dimValue);
    const withMm = normalised.toLowerCase().endsWith('mm') ? normalised : `${normalised}mm`;
    return base.replace(/(x{2,}mm?)/gi, withMm);
  }
  return base;
}

/**
 * Helper: Replace dimension patterns in label with actual values
 */
function replaceDimensionPatterns(
  label: string,
  fieldName: string,
  stepName: string,
  dimensionValues?: Record<string, string>,
  columnHeaders?: Record<string, string>
): string {
  // Only apply to Plot Drives, Plot Pavings, Scaffold, and Garden
  // Check if we have either dimensionValues or columnHeaders to work with
  if (!dimensionValues && !columnHeaders) {
    return label;
  }

  let fieldKey: string | undefined;

  if (stepName === 'Plot Drives') {
    // Get the column key from the semantic field name for Plot Drives
    fieldKey = PLOT_DRIVES_REVERSE_MAPPING[fieldName];
  } else if (stepName === 'Plot Pavings') {
    // For Plot Pavings, field names are already the keys
    fieldKey = PLOT_PAVINGS_REVERSE_MAPPING[fieldName];
  } else if (stepName === 'Scaffold') {
    // For Scaffold, field names are already the keys
    fieldKey = SCAFFOLD_REVERSE_MAPPING[fieldName];
  } else if (stepName === 'Garden') {
    // For Garden, field names are already the keys
    fieldKey = GARDEN_REVERSE_MAPPING[fieldName];
  } else if (stepName === 'Foundations') {
    // For Foundations, field names are already the keys
    fieldKey = FOUNDATIONS_REVERSE_MAPPING[fieldName];
  } else {
    return label;
  }

  if (!fieldKey) {
    return label;
  }

  // If columnHeaders has a dynamic header for this field, use it directly.
  // This handles cases like Sweep & Tack where the label is from a dropdown selection.
  // Persisted headers built before the bill-summary normalisation existed (e.g.
  // "0.150mm thick MOT Type 1") are repaired in-place via `normaliseDecimalMmInLabel`.
  if (columnHeaders && columnHeaders[fieldKey]) {
    return normaliseDecimalMmInLabel(columnHeaders[fieldKey]);
  }

  // Otherwise, replace dimension patterns with actual value
  if (!dimensionValues || !dimensionValues[fieldKey]) {
    return label;
  }

  // Decimal entries (e.g. "0.150") are converted to mm for display via
  // `formatDimensionLabel` so bill summary labels always show millimetre values.
  const dimensionValue = formatDimensionLabel(dimensionValues[fieldKey]);
  const replacedLabel = label.replace(/(x{2,}mm?)/gi, (match) => {
    let value = dimensionValue;
    // Add 'mm' suffix if pattern had it and value doesn't
    if (match.toLowerCase().includes('mm') && !value.toLowerCase().endsWith('mm')) {
      value = value + 'mm';
    }
    return value;
  });
  return replacedLabel;
}

/**
 * Helper: Get mapping data from rates (API or JSON fallback)
 */
function getMappingForField(
  stepName: string,
  fieldName: string,
  ratesMapping?: RateMappingJSON | null
): { description: string; rate: number; unit: string } | null {
  // Use provided rates or fallback to JSON
  const groundworksRatesMapping = ratesMapping || groundworksRatesMappingJSON;
  // Map step names to mapping keys
  const stepToMappingKey: { [key: string]: string } = {
    'Foundations': 'foundations',
    'Cut & Fills': 'cutFills',
    'Drainage': 'drainage',
    'Plot Drives': 'plotDrives',
    'Private Drives Surface': 'plotDrives',
    'Shared Drives Surface': 'plotDrives',
    'Plot Service': 'plotService',
    'Plot Pavings': 'plotPavings',
    'Scaffold': 'scaffold',
    'Garden': 'garden',
    'Ret & Screen Walls': 'retainingWalls',
    'Geowall & Tobermore Walls': 'geoToberWall',
    'External Works': 'externalWorks',
  };

  const mappingKey = stepToMappingKey[stepName];
  if (!mappingKey || !groundworksRatesMapping[mappingKey]) {
    return null;
  }

  const mappingArray = groundworksRatesMapping[mappingKey];
  // Generate the readable field name for better matching
  const readableFieldName = generateLabelFromFieldName(fieldName);
  // Try multiple matching strategies
  let mapping = mappingArray.find((item: any) => {
    const frontendLabel = item.frontend_label || '';
    const itemName = item.name || '';

    // Strategy 1: Exact match with frontend_label
    if (frontendLabel === fieldName) return true;

    // Strategy 2: Exact match with name
    if (itemName === fieldName) return true;

    // Strategy 3: Case-insensitive match with readable field name and frontend_label
    if (frontendLabel.toLowerCase() === readableFieldName.toLowerCase()) return true;

    // Strategy 4: Case-insensitive match with readable field name and name
    if (itemName.toLowerCase() === readableFieldName.toLowerCase()) return true;

    // Strategy 5: Remove units and special chars for matching
    const cleanFrontendLabel = frontendLabel.replace(/\s*\([^)]*\)/g, '').trim().toLowerCase();
    const cleanReadableFieldName = readableFieldName.replace(/\s*\([^)]*\)/g, '').trim().toLowerCase();
    if (cleanFrontendLabel === cleanReadableFieldName) return true;

    // Strategy 6: Normalize spaces and special characters
    const normalizeFrontendLabel = frontendLabel.toLowerCase().replace(/[^a-z0-9]/g, '');
    const normalizeReadableFieldName = readableFieldName.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (normalizeFrontendLabel === normalizeReadableFieldName) return true;

    // Strategy 7: Normalize name
    const normalizeItemName = itemName.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (normalizeItemName === normalizeReadableFieldName) return true;

    return false;
  });

  if (mapping) {
    return {
      description: removeUnitFromLabel(mapping.evaluate_description || mapping.frontend_label || mapping.name),
      rate: mapping.evaluate_rate || 0,
      unit: mapping.evaluate_unit || 'Nr'
    };
  }
  return null;
}

/**
 * Get all columns with rates for a specific step from groundworks data
 */
export function getBOQColumnsForStep(
  groundworksData: any,
  stepName: string,
  dimensionValues?: Record<string, string>,
  ratesMapping?: RateMappingJSON | null,
  columnHeaders?: Record<string, string>
): BOQColumn[] {
  const columns: BOQColumn[] = [];

  // Map step names to their table keys
  const tableKeyMap: { [key: string]: string } = {
    'Foundations': 'foundationsTable',
    'Cut & Fills': 'cutFillsTable',
    'Drainage': 'drainageTableStandard',
    'Plot Drives': 'plotDrivesTable',
    'Private Drives Surface': 'plotDrivesTable',
    'Shared Drives Surface': 'plotDrivesTable',
    'Plot Service': 'plotServiceTable',
    'Plot Pavings': 'plotPavingsTable',
    'Scaffold': 'scaffoldTable',
    'Garden': 'gardenTable',
    'Ret & Screen Walls': 'retainingWallsTable',
    'Geowall & Tobermore Walls': 'geoToberWallTable',
    'External Works': 'externalWorksTable',
  };

  const tableKey = tableKeyMap[stepName];
  if (!tableKey || !groundworksData?.[tableKey]) {
    return [];
  }

  let tableData = groundworksData[tableKey];
  if (!Array.isArray(tableData) || tableData.length === 0) {
    return [];
  }

  // Transform Plot Drives data from column format to semantic format
  if (stepName === 'Plot Drives' || stepName === 'Private Drives Surface' || stepName === 'Shared Drives Surface') {
    tableData = transformPlotDrivesData(tableData);
  }

  // Filter out totals rows
  tableData = tableData.filter((row: any) =>
    row.key !== 'totals' &&
    row.plotNo !== 'TOTALS' &&
    row.plotNo !== 'Total' &&
    row.plotNo !== 'totals'
  );

  // Get the first row to extract field structure
  const firstRow = tableData[0];

  // Excluded system fields
  const excludedFields = ['key', 'id', '_id', 'createdAt', 'updatedAt', 'plotNo', 'housetype', 'foundationType'];

  // Extract all numeric fields
  Object.keys(firstRow).forEach((fieldName) => {
    if (excludedFields.includes(fieldName)) return;

    // Only include NUMERIC fields
    const sampleValue = firstRow[fieldName];
    const isNumericField =
      typeof sampleValue === 'number' ||
      (!isNaN(parseFloat(sampleValue)) && sampleValue !== '');

    if (!isNumericField) return;

    // Check if any row has a non-zero value for this numeric field
    const hasData = tableData.some((row) => {
      const value = row[fieldName];
      if (value === undefined || value === null) return false;
      const numValue = typeof value === 'number' ? value : parseFloat(value);
      return !isNaN(numValue) && numValue !== 0;
    });

    // For Plot Drives, surface steps and Plot Service, always include all fields even if no data (to show full rate structure)
    const forceInclude = stepName === 'Plot Drives' || stepName === 'Private Drives Surface' || stepName === 'Shared Drives Surface' || stepName === 'Plot Service';

    if (hasData || forceInclude) {
      let label: string;
      let rate: number;
      let unit: string;

      // For Plot Pavings, use exact field labels from mapping
      if (stepName === 'Plot Pavings') {
        const dynamicHeader = columnHeaders?.[fieldName];
        const plotPavingsLabel = getPlotPavingsFieldLabel(fieldName);
        if (plotPavingsLabel) {
          // Use dynamic column header (e.g. '601.2mm thick Stone bed') when available
          label = dynamicHeader || removeUnitFromLabel(plotPavingsLabel);
          // Get unit from Plot Pavings field mapping
          const plotPavingsUnit = getPlotPavingsFieldUnit(fieldName);
          unit = plotPavingsUnit ?? '';

          // Try rate lookup - prefer dynamic header, fall back to static label
          const labelForLookup = dynamicHeader || plotPavingsLabel;
          const mappingData = getMappingForField(stepName, labelForLookup, ratesMapping);
          if (mappingData) {
            rate = mappingData.rate;
            unit = mappingData.unit;
          } else {
            // No rate found in mapping, use 0 as fallback
            rate = 0;
          }
        } else {
          // Fallback if field not found in mapping
          const labelForLookup = dynamicHeader || fieldName;
          const mappingData = getMappingForField(stepName, labelForLookup, ratesMapping);
          if (mappingData) {
            label = dynamicHeader || generateLabelFromFieldName(fieldName);
            rate = mappingData.rate;
            unit = mappingData.unit;
          } else {
            label = dynamicHeader || generateLabelFromFieldName(fieldName);
            unit = inferUnitFromFieldName(fieldName, stepName);
            rate = 0; // Use 0 instead of random rate
          }
        }
      } else if (stepName === 'Scaffold') {
        // For Scaffold, use exact field labels from mapping
        const scaffoldLabel = getScaffoldFieldLabel(fieldName);
        if (scaffoldLabel) {
          label = resolveDynamicLabel(scaffoldLabel, fieldName, dimensionValues, columnHeaders);
          // Get unit from Scaffold field mapping
          const scaffoldUnit = getScaffoldFieldUnit(fieldName);
          unit = scaffoldUnit || 'm²'; // Default to m² if not found

          // Try to get rate from mapping using the label
          const mappingData = getMappingForField(stepName, scaffoldLabel, ratesMapping);
          if (mappingData) {
            rate = mappingData.rate;
            unit = mappingData.unit;
          } else {
            // No rate found in mapping, use 0 as fallback
            rate = 0;
          }
        } else {
          // Fallback if field not found in mapping
          const mappingData = getMappingForField(stepName, fieldName, ratesMapping);
          if (mappingData) {
            label = generateLabelFromFieldName(fieldName);
            rate = mappingData.rate;
            unit = mappingData.unit;
          } else {
            label = generateLabelFromFieldName(fieldName);
            unit = inferUnitFromFieldName(fieldName, stepName);
            rate = 0; // Use 0 instead of random rate
          }
        }
      } else if (stepName === 'Garden') {
        // For Garden, use exact field labels from mapping
        const gardenLabel = getGardenFieldLabel(fieldName);
        if (gardenLabel) {
          label = resolveDynamicLabel(gardenLabel, fieldName, dimensionValues, columnHeaders);
          // Get unit from Garden field mapping
          const gardenUnit = getGardenFieldUnit(fieldName);
          unit = gardenUnit || 'm²'; // Default to m² if not found

          // Try to get rate from mapping using the label
          const mappingData = getMappingForField(stepName, gardenLabel, ratesMapping);
          if (mappingData) {
            rate = mappingData.rate;
            unit = mappingData.unit;
          } else {
            // No rate found in mapping, use 0 as fallback
            rate = 0;
          }
        } else {
          // Fallback if field not found in mapping
          const mappingData = getMappingForField(stepName, fieldName, ratesMapping);
          if (mappingData) {
            label = generateLabelFromFieldName(fieldName);
            rate = mappingData.rate;
            unit = mappingData.unit;
          } else {
            label = generateLabelFromFieldName(fieldName);
            unit = inferUnitFromFieldName(fieldName, stepName);
            rate = 0; // Use 0 instead of random rate
          }
        }
      } else if (stepName === 'Plot Drives' || stepName === 'Private Drives Surface' || stepName === 'Shared Drives Surface') {
        // For Plot Drives and surface steps, use exact field labels from mapping
        const plotDrivesLabel = getPlotDrivesFieldLabel(fieldName);

        // Check if we have a dynamic header for this field (from dropdown selection)
        const fieldKey = PLOT_DRIVES_REVERSE_MAPPING[fieldName];
        const dynamicHeader = fieldKey && columnHeaders ? columnHeaders[fieldKey] : undefined;

        if (plotDrivesLabel) {
          // Use dynamic header (which has actual thickness/type resolved) if available, otherwise static label
          label = removeUnitFromLabel(dynamicHeader || plotDrivesLabel);
          // Get unit from Plot Drives field mapping
          const plotDrivesUnit = getPlotDrivesFieldUnit(fieldName);
          unit = plotDrivesUnit || 'm²'; // Default to m² if not found

          // If there's a dynamic header (from dropdown selection), use it for rate lookup
          const labelForRateLookup = dynamicHeader || plotDrivesLabel;

          // Try to get rate from mapping using the label
          const mappingData = getMappingForField(stepName, labelForRateLookup, ratesMapping);
          if (mappingData) {
            rate = mappingData.rate;
            unit = mappingData.unit;
          } else {
            // No rate found in mapping, use 0 as fallback
            rate = 0;
          }
        } else {
          // Fallback if field not found in mapping
          const labelForRateLookup = dynamicHeader || fieldName;
          const mappingData = getMappingForField(stepName, labelForRateLookup, ratesMapping);
          if (mappingData) {
            label = dynamicHeader || generateLabelFromFieldName(fieldName);
            rate = mappingData.rate;
            unit = mappingData.unit;
          } else {
            label = generateLabelFromFieldName(fieldName);
            unit = inferUnitFromFieldName(fieldName, stepName);
            rate = 0; // Use 0 instead of random rate
          }
        }
      } else if (stepName === 'Drainage') {
        // For Drainage, use exact field labels from mapping
        const drainageLabel = getDrainageFieldLabel(fieldName);
        if (drainageLabel) {
          label = removeUnitFromLabel(drainageLabel);
          // Get unit from Drainage field mapping
          const drainageUnit = getDrainageFieldUnit(fieldName);
          unit = drainageUnit || 'm²'; // Default to m² if not found

          // Try to get rate from mapping using the label
          const mappingData = getMappingForField(stepName, drainageLabel, ratesMapping);
          if (mappingData) {
            rate = mappingData.rate;
            unit = mappingData.unit;
          } else {
            // No rate found in mapping, use 0 as fallback
            rate = 0;
          }
        } else {
          // Fallback if field not found in mapping
          const mappingData = getMappingForField(stepName, fieldName, ratesMapping);
          if (mappingData) {
            label = generateLabelFromFieldName(fieldName);
            rate = mappingData.rate;
            unit = mappingData.unit;
          } else {
            label = generateLabelFromFieldName(fieldName);
            unit = inferUnitFromFieldName(fieldName, stepName);
            rate = 0; // Use 0 instead of random rate
          }
        }
      } else if (stepName === 'Ret & Screen Walls') {
        // For Ret & Screen Walls, use exact field labels from mapping
        const retainingWallsLabel = getRetainingWallsFieldLabel(fieldName);
        if (retainingWallsLabel) {
          label = removeUnitFromLabel(retainingWallsLabel);
          // Get unit from Retaining Walls field mapping
          const retainingWallsUnit = getRetainingWallsFieldUnit(fieldName);
          unit = retainingWallsUnit || 'm'; // Default to m if not found

          // Try to get rate from mapping using the label
          const mappingData = getMappingForField(stepName, retainingWallsLabel, ratesMapping);
          if (mappingData) {
            rate = mappingData.rate;
            unit = mappingData.unit;
          } else {
            // No rate found in mapping, use 0 as fallback
            rate = 0;
          }
        } else {
          // Fallback if field not found in mapping
          const mappingData = getMappingForField(stepName, fieldName, ratesMapping);
          if (mappingData) {
            label = generateLabelFromFieldName(fieldName);
            rate = mappingData.rate;
            unit = mappingData.unit;
          } else {
            label = generateLabelFromFieldName(fieldName);
            unit = inferUnitFromFieldName(fieldName, stepName);
            rate = 0; // Use 0 instead of random rate
          }
        }
      } else if (stepName === 'Plot Service') {
        // For Plot Service, use exact field labels from mapping
        const plotServiceLabel = getPlotServiceFieldLabel(fieldName);
        if (plotServiceLabel) {
          label = removeUnitFromLabel(plotServiceLabel); // Remove unit from description
          // Get unit from Plot Service field mapping
          const plotServiceUnit = getPlotServiceFieldUnit(fieldName);
          unit = plotServiceUnit || ''; // Default to empty if not found

          // Try to get rate from mapping using the label
          const mappingData = getMappingForField(stepName, plotServiceLabel, ratesMapping);
          if (mappingData) {
            rate = mappingData.rate;
            // Keep the table column name as description, only take rate and unit from mapping
            unit = mappingData.unit;
          } else {
            // No rate found in mapping, use 0 as fallback
            rate = 0;
          }
        } else {
          // Fallback if field not found in mapping
          const mappingData = getMappingForField(stepName, fieldName, ratesMapping);
          if (mappingData) {
            label = generateLabelFromFieldName(fieldName);
            rate = mappingData.rate;
            unit = mappingData.unit;
          } else {
            label = generateLabelFromFieldName(fieldName);
            unit = inferUnitFromFieldName(fieldName, stepName);
            rate = 0; // Use 0 instead of random rate
          }
        }
      } else if (stepName === 'Geowall & Tobermore Walls') {
        // For Geowall & Tobermore Walls, use exact field labels from mapping
        const geoToberWallLabel = getGeoToberWallFieldLabel(fieldName);
        if (geoToberWallLabel) {
          label = removeUnitFromLabel(geoToberWallLabel);
          // Get unit from Geowall & Tobermore Walls field mapping
          const geoToberWallUnit = getGeoToberWallFieldUnit(fieldName);
          unit = geoToberWallUnit || 'm³'; // Default to m³ if not found

          // Try to get rate from mapping using the label
          const mappingData = getMappingForField(stepName, geoToberWallLabel, ratesMapping);
          if (mappingData) {
            rate = mappingData.rate;
            unit = mappingData.unit;
          } else {
            // No rate found in mapping, use 0 as fallback
            rate = 0;
          }
        } else {
          // Fallback if field not found in mapping
          const mappingData = getMappingForField(stepName, fieldName, ratesMapping);
          if (mappingData) {
            label = generateLabelFromFieldName(fieldName);
            rate = mappingData.rate;
            unit = mappingData.unit;
          } else {
            label = generateLabelFromFieldName(fieldName);
            unit = inferUnitFromFieldName(fieldName, stepName);
            rate = 0; // Use 0 instead of random rate
          }
        }
      } else if (stepName === 'Foundations') {
        // For Foundations, use exact field labels from mapping
        // Get mesh reinforcement type, substructure block type, and prefix values from groundworks data if available
        const meshReinforcementType = groundworksData?.meshReinforcementType || undefined;
        const substructureBlockType = (groundworksData as any)?.substructureBlockType || undefined;
        const prefixValues = (groundworksData as any)?.foundationsPrefixValues || undefined;
        const foundationsLabel = getFoundationsFieldLabel(fieldName, meshReinforcementType, substructureBlockType, prefixValues);
        if (foundationsLabel) {
          label = removeUnitFromLabel(foundationsLabel);
          // Get unit from Foundations field mapping
          const foundationsUnit = getFoundationsFieldUnit(fieldName);
          unit = foundationsUnit || 'm²'; // Default to m² if not found

          // Try to get rate from mapping using the label
          const mappingData = getMappingForField(stepName, foundationsLabel, ratesMapping);
          if (mappingData) {
            rate = mappingData.rate;
            unit = mappingData.unit;
          } else {
            // No rate found in mapping, use 0 as fallback
            rate = 0;
          }
        } else {
          // Fallback if field not found in mapping
          const mappingData = getMappingForField(stepName, fieldName, ratesMapping);
          if (mappingData) {
            label = generateLabelFromFieldName(fieldName);
            rate = mappingData.rate;
            unit = mappingData.unit;
          } else {
            label = generateLabelFromFieldName(fieldName);
            unit = inferUnitFromFieldName(fieldName, stepName);
            rate = 0; // Use 0 instead of random rate
          }
        }

        // Force rate to 0 for specific foundation fields that should be excluded from pricing
        // (Dense/Aerated concrete blockwork 100mm Ext leaf, Aerated HB common brickwork 100mm)
        if (FOUNDATIONS_ZERO_RATE_FIELDS.includes(fieldName)) {
          rate = 0;
        }
      } else {
        // For other steps, use existing logic
        const mappingData = getMappingForField(stepName, fieldName, ratesMapping);
        if (mappingData) {
          label = generateLabelFromFieldName(fieldName);
          rate = mappingData.rate;
          unit = mappingData.unit;
        } else {
          // Fallback to generated values if not found
          label = generateLabelFromFieldName(fieldName);
          unit = inferUnitFromFieldName(fieldName, stepName);
          rate = 0; // Use 0 instead of random rate
        }
      }

      // Replace dimension patterns with actual values for Plot Drives, Plot Pavings, and Scaffold
      label = replaceDimensionPatterns(label, fieldName, stepName, dimensionValues, columnHeaders);

      columns.push({
        key: `${stepName}-${fieldName}`,
        fieldName,
        label,
        unit,
        rate,
        stepName
      });
    }
  });

  return columns;
}

/**
 * Calculate total quantities and amounts for a column across all rows
 */
export function calculateBOQTotals(
  groundworksData: any,
  stepName: string,
  columns: BOQColumn[],
  dimensionValues?: Record<string, string>,
  columnHeaders?: Record<string, string>
): BOQSummaryItem[] {
  const items: BOQSummaryItem[] = [];

  const tableKeyMap: { [key: string]: string } = {
    'Foundations': 'foundationsTable',
    'Cut & Fills': 'cutFillsTable',
    'Drainage': 'drainageTableStandard',
    'Plot Drives': 'plotDrivesTable',
    'Private Drives Surface': 'plotDrivesTable',
    'Shared Drives Surface': 'plotDrivesTable',
    'Plot Service': 'plotServiceTable',
    'Plot Pavings': 'plotPavingsTable',
    'Scaffold': 'scaffoldTable',
    'Garden': 'gardenTable',
    'Ret & Screen Walls': 'retainingWallsTable',
    'Geowall & Tobermore Walls': 'geoToberWallTable',
    'External Works': 'externalWorksTable',
  };

  const tableKey = tableKeyMap[stepName];
  if (!tableKey || !groundworksData?.[tableKey]) {
    return [];
  }

  // Transform Plot Drives data from column format to semantic format
  let tableData = groundworksData[tableKey];
  if (stepName === 'Plot Drives' || stepName === 'Private Drives Surface' || stepName === 'Shared Drives Surface') {
    tableData = transformPlotDrivesData(tableData);
  }

  // Filter out totals rows
  tableData = tableData.filter((row: any) =>
    row.key !== 'totals' &&
    row.plotNo !== 'TOTALS' &&
    row.plotNo !== 'Total' &&
    row.plotNo !== 'totals'
  );

  columns.forEach((col) => {
    let totalQuantity = 0;

    // Sum quantities across all rows (field values are the quantities directly)
    tableData.forEach((row: any) => {
      const value = row[col.fieldName];
      if (value !== undefined && value !== null) {
        const numValue = typeof value === 'number' ? value : parseFloat(value);
        if (!isNaN(numValue)) {
          totalQuantity += numValue;
        }
      }
    });

    // Always include items regardless of quantity to show full rate structure
    const totalAmount = totalQuantity * col.rate;

    items.push({
      key: col.key,
      fieldName: col.fieldName,
      description: col.label,
      quantity: totalQuantity,
      unit: col.unit,
      rate: col.rate,
      amount: totalAmount
    });
  });

  return items;
}

/**
 * Generate BOQ summary items grouped by category
 */
export function generateBOQSummaryWithCategories(
  groundworksData: any,
  stepName: string,
  categories: { [categoryName: string]: string[] },
  dimensionValues?: Record<string, string>,
  ratesMapping?: RateMappingJSON | null,
  columnHeaders?: Record<string, string>
): BOQSummaryItem[] {
  const columns = getBOQColumnsForStep(groundworksData, stepName, dimensionValues, ratesMapping, columnHeaders);
  const allItems = calculateBOQTotals(groundworksData, stepName, columns, dimensionValues, columnHeaders);

  const categorizedItems: BOQSummaryItem[] = [];

  Object.entries(categories).forEach(([categoryName, fieldNames]) => {
    const categoryItems = allItems.filter(item =>
      fieldNames.includes(item.fieldName)
    );

    categoryItems.forEach(item => {
      categorizedItems.push({
        ...item,
        category: categoryName
      });
    });
  });

  return categorizedItems;
}

/**
 * Generate BOQ summary items without categories
 */
export function generateBOQSummary(
  groundworksData: any,
  stepName: string,
  dimensionValues?: Record<string, string>,
  ratesMapping?: RateMappingJSON | null,
  columnHeaders?: Record<string, string>
): BOQSummaryItem[] {
  const columns = getBOQColumnsForStep(groundworksData, stepName, dimensionValues, ratesMapping, columnHeaders);
  return calculateBOQTotals(groundworksData, stepName, columns, dimensionValues, columnHeaders);
}

/**
 * Helper: Infer unit from field name and step
 */
function inferUnitFromFieldName(fieldName: string, stepName: string): string {
  const lowerField = fieldName.toLowerCase();

  // Common unit patterns
  if (lowerField.includes('excavat') || lowerField.includes('concrete') || lowerField.includes('fill')) {
    return 'm³';
  }
  if (lowerField.includes('blockwork') || lowerField.includes('brick') || lowerField.includes('wall')) {
    return 'm²';
  }
  if (lowerField.includes('dpc') || lowerField.includes('membrane') || lowerField.includes('insulation')) {
    return 'm²';
  }
  if (lowerField.includes('lintel') || lowerField.includes('vent') || lowerField.includes('door')) {
    return 'Nr';
  }
  if (lowerField.includes('length') || lowerField.includes('pipe') || lowerField.includes('drain')) {
    return 'm';
  }

  // Step-specific defaults
  if (stepName === 'Cut & Fills') {
    return 'm³';
  }
  if (stepName === 'Drainage') {
    return 'm';
  }

  // Default
  return 'Nr';
}
